/**
 * V3 search-listings transforms. The V3 `HpBuildingGroupDto` is the SRP's new
 * listings payload (from `searchListings`). These helpers bridge it to what the
 * SRP grid / counts / map need:
 *  - construct photo URLs from the `{baseUrl, photoIds, formats}` parts
 *  - cap photo arrays before the RSC / React Query boundary
 *  - derive pagination counts
 *
 * The card-input mapping (`toCardListing`) and map-marker builders live with the
 * consumer wiring; this module holds the pure, framework-neutral pieces.
 */
import type { PartialDeep } from 'type-fest';
import type { HpBuildingDto, HpBuildingGroupDto, HpListingCardDto } from '@zg-rentals/ts-hotpads-api-client';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';
import { getSrpMaxPageLimit } from '@/src/lib/seo/srp-pagination';
import { generateMarkerId } from './generateMarkerId';
import { normalizeListingHref } from './listingHref';
import { normalizeTotalMonthlyPriceDisplayInfo } from './totalMonthlyPrice';
import { hasV3ListingTag } from './v3ListingTags';
import { normalizeV3Contact } from './v3Contact';
import type { Contact } from '@/src/types/listingDetails.type';

type HpListingPhotos = NonNullable<HpListingCardDto['photos']>;

type HpListingCardDtoWithContact = HpListingCardDto & {
  contact?: Contact;
  contactPhone?: string | null;
};

const n2u = (value: number | null | undefined): number | undefined => (value == null ? undefined : value);

/** Mirrors the V2 cap (see listingTransforms.LISTING_PHOTO_CAP). */
export const V3_PHOTO_CAP = 12;

type PhotoSize = keyof NonNullable<HpListingPhotos['formats']>;

function normalizeCardBuildingHref(uri: string | undefined | null): string | null {
  const href = normalizeListingHref(uri);
  if (!href) return null;

  const [path] = href.split('?');
  const [slug] = path.split('/').filter(Boolean);
  return slug ? `/${slug}/building` : null;
}

/**
 * Builds ordered photo URLs for a listing card from the V3 photo parts.
 * URL = `${baseUrl}${photoId}${formats[size]}` (the live format suffix already
 * encodes size + extension, e.g. `-rentals_medium_500_500.webp`). The hero
 * photo is placed first. Returns `[]` when parts are missing.
 */
export function buildV3PhotoUrls(
  photos: PartialDeep<HpListingPhotos> | null | undefined,
  size: PhotoSize = 'medium',
): Array<string> {
  if (!photos?.baseUrl || !photos.photoIds?.length) return [];
  const suffix = photos.formats?.[size] ?? '';
  const ids = [...photos.photoIds];
  if (photos.heroId) {
    const heroIdx = ids.indexOf(photos.heroId);
    if (heroIdx > 0) {
      ids.splice(heroIdx, 1);
      ids.unshift(photos.heroId);
    } else if (heroIdx === -1) {
      ids.unshift(photos.heroId);
    }
  }
  return ids.map((id) => `${photos.baseUrl}${id}${suffix}`);
}

function capBuildingPhotos(building: HpBuildingDto, cap: number): HpBuildingDto {
  if (!building.listings?.length) return building;
  return {
    ...building,
    listings: building.listings.map((listing) => {
      if (!listing.photos?.photoIds || listing.photos.photoIds.length <= cap) return listing;
      return { ...listing, photos: { ...listing.photos, photoIds: listing.photos.photoIds.slice(0, cap) } };
    }),
  };
}

/**
 * Caps each listing's `photos.photoIds` so the SRP doesn't ship dozens of photo
 * ids per card across the RSC / React Query boundary. Mirrors `capResponsePhotos`.
 */
export function capV3Photos(group: HpBuildingGroupDto, cap?: number): HpBuildingGroupDto;
export function capV3Photos(
  group: HpBuildingGroupDto | null | undefined,
  cap?: number,
): HpBuildingGroupDto | null | undefined;
export function capV3Photos(
  group: HpBuildingGroupDto | null | undefined,
  cap: number = V3_PHOTO_CAP,
): HpBuildingGroupDto | null | undefined {
  if (!group?.buildings?.length) return group;
  return { ...group, buildings: group.buildings.map((b) => capBuildingPhotos(b, cap)) };
}

/**
 * Drops fields the SRP grid never reads (`listedBy`, `contactForm`, and the
 * JSON-LD-only `tours`) before the V3 group crosses into the client. Photo
 * capping is applied separately via `capV3Photos`.
 */
export function pruneV3ClientListings(group: HpBuildingGroupDto): HpBuildingGroupDto;
export function pruneV3ClientListings(group: HpBuildingGroupDto | null | undefined): HpBuildingGroupDto | null;
export function pruneV3ClientListings(
  group: HpBuildingGroupDto | null | undefined,
): HpBuildingGroupDto | null {
  if (!group) return null;
  if (!group.buildings?.length) return group;
  return {
    ...group,
    buildings: group.buildings.map((building) => ({
      ...building,
      listings: (building.listings ?? []).map((card) => {
        const next: HpListingCardDtoWithContact = {
          ...card,
          contact: normalizeV3Contact(card.contactForm),
          contactPhone: card.listedBy?.contactPhone || card.listedBy?.companyPhoneNumber || null,
        };
        // listedBy/contactForm are required on the generated card type; retain
        // the card contact fields above, then strip the raw payloads via a partial cast.
        delete (next as Partial<HpListingCardDto>).listedBy;
        delete (next as Partial<HpListingCardDto>).contactForm;
        delete (next as Partial<HpListingCardDto>).tours;
        return next;
      }),
    })),
  };
}

export type PaginationCounts = {
  totalBuildingsAvailable: number;
  totalListingsAvailable: number;
  totalPages: number;
};

/**
 * Pagination counts from the V3 group. The SRP renders one card per *building*
 * (a page returns `numBuildingsAvailable`-bounded buildings, `perPage` at a
 * time), so pagination divides by `numBuildingsAvailable`. `numUnits` is the
 * larger "N rentals available" display total (units across all buildings) and
 * is NOT the pagination divisor. Confirmed against live data: a 40-limit page
 * returned 40 buildings with numBuildingsAvailable=1354, numUnits=4215.
 *
 * `totalPages` is capped at SRP_MAX_PAGE_LIMIT, matching legacy hotpads-web;
 * pass `searchSlugOrPath` so affordable/luxury slugs get their halved limit.
 */
export function extractV3PaginationCounts(
  group: HpBuildingGroupDto | null | undefined,
  perPage: number,
  searchSlugOrPath?: string,
): PaginationCounts {
  const totalBuildingsAvailable = group?.numBuildingsAvailable ?? 0;
  const totalListingsAvailable = group?.numUnits ?? totalBuildingsAvailable;
  // Fall back to the units total only when the building count is absent — a
  // present-but-zero building count must not switch the divisor to the ~3x
  // larger units total and mint phantom pages (rel=next / jump-to-last are
  // built from totalPages).
  const pageable = group?.numBuildingsAvailable ?? totalListingsAvailable;
  const uncappedTotalPages = pageable <= 0 ? 1 : Math.max(Math.ceil(pageable / perPage), 1);
  const totalPages = getSrpMaxPageLimit(uncappedTotalPages, searchSlugOrPath);
  return { totalBuildingsAvailable, totalListingsAvailable, totalPages };
}

/**
 * Maps a V3 listing card (+ its building) to the `ExtendedListing` shape the
 * shared `ListingCard` / map markers already consume. We map into the existing
 * card shape (rather than rewriting the card) because `ListingCard` is shared
 * with homepage + HDP recommendations, which stay on V2.
 *
 * Price/beds/baths/sqft go into `modelSummary` (numeric ranges) — the formatters
 * (`useListingFormatters`) derive display strings from those. Status badges come
 * from the V3 `tags[]` array. `listedBy`/`amenities` are intentionally omitted:
 * `listedBy` is pruned from SRP client data, and the V3 card DTO carries no
 * amenities (cards drop amenity tags on the V3 path — see plan risks).
 */
export function toCardListing(card: HpListingCardDtoWithContact, building: HpBuildingDto): ExtendedListing {
  const tags = card.tags ?? [];
  const photoUrls = buildV3PhotoUrls(card.photos);
  const s = card.summary;
  const unitCount = card.unitCount ?? undefined;
  const tours = (card.tours ?? []).filter((tour) => Boolean(tour.url));
  const isGroupedBuildingCard = (building.listings?.length ?? 0) > 1;
  const parentBuildingHref = normalizeListingHref(building.uri);
  const buildingHref = isGroupedBuildingCard
    ? parentBuildingHref || normalizeCardBuildingHref(card.uriMalone)
    : parentBuildingHref;
  const opensBuildingPage = Boolean(buildingHref && isGroupedBuildingCard);

  // ExtendedListing (via ListingDetails) types many fields as required; V3 fields
  // are optional and the shared card reads them defensively. Build as Partial to
  // keep field-name/type checking, then widen on return.
  const listing: Partial<ExtendedListing> = {
    uriMalone: card.uriMalone ?? undefined,
    aliasEncoded: card.listingAlias ?? undefined,
    maloneLotIdEncoded: building.lotIdEncoded ?? undefined,
    lotIdEncoded: building.lotIdEncoded ?? undefined,
    geo: building.geo ? { lat: building.geo.lat ?? 0, lon: building.geo.lon ?? 0 } : undefined,
    unit: card.unit ?? undefined,
    listingType: card.listingType ?? undefined,
    propertyType: card.propertyType ?? undefined,
    title: card.title ?? card.name ?? undefined,
    buildingHref: buildingHref ?? undefined,
    opensBuildingPage,
    uriBuilding: buildingHref ?? undefined,
    searchKeyword: card.searchKeyword ?? undefined,
    contact: card.contact ?? normalizeV3Contact(card.contactForm),
    contactPhone:
      card.contactPhone ?? card.listedBy?.contactPhone ?? card.listedBy?.companyPhoneNumber ?? undefined,
    medPhotoUrls: photoUrls,
    medPhotoUrl: photoUrls[0],
    photoCount: card.photos?.photoIds?.length,
    address: card.address
      ? {
          street: card.address.street ?? '',
          city: card.address.city ?? '',
          state: card.address.state ?? '',
          zip: card.address.zip ?? '',
          neighborhood: '',
          // v3 moved street-hiding off the address onto the `streetHidden` listing tag.
          hideStreet: hasV3ListingTag(tags, 'streetHidden'),
        }
      : undefined,
    modelSummary: s
      ? {
          minPrice: n2u(s.price?.min),
          maxPrice: n2u(s.price?.max),
          minBeds: n2u(s.beds?.min),
          maxBeds: n2u(s.beds?.max),
          minBaths: n2u(s.baths?.min),
          maxBaths: n2u(s.baths?.max),
          minSqft: n2u(s.sqft?.min),
          maxSqft: n2u(s.sqft?.max),
        }
      : undefined,
    modelTypeUnitCount: unitCount,
    newUnitCount: card.newUnitCount ?? undefined,
    priceDropCount: card.priceDropCount ?? undefined,
    specialOfferLabel: card.specialOfferLabel ?? undefined,
    hasSpecialOffers: hasV3ListingTag(tags, 'specialOffers') || Boolean(card.specialOfferLabel),
    has3dTour: hasV3ListingTag(tags, 'zillow3dTour') || hasV3ListingTag(tags, 'external3dTour') || (card.tours?.length ?? 0) > 0,
    tourUrls: tours.length ? tours.map((tour) => tour.url) : undefined,
    tourLabels: tours.length ? tours.map((tour) => tour.label ?? '') : undefined,
    isPaidAndNotDemoted: hasV3ListingTag(tags, 'paidAndNotDemoted'),
    isDemoted: hasV3ListingTag(tags, 'demoted'),
    isClaimedFreeListing: hasV3ListingTag(tags, 'claimedFreeListing'),
    isPaidMultifamily: hasV3ListingTag(tags, 'paidMultifamily'),
    trusted: hasV3ListingTag(tags, 'trusted'),
    hasTotalMonthlyPrice: hasV3ListingTag(tags, 'totalMonthlyPrice'),
    totalMonthlyPriceDisplayInfo: normalizeTotalMonthlyPriceDisplayInfo(card.totalMonthlyPriceDisplayInfo),
    userItemTypes: card.userItemTypes ?? undefined,
    rentalApplicationStatus: card.rentalApplicationStatus ?? undefined,
    activated: card.activated ?? undefined,
    updated: card.updated ?? undefined,
    active: true,
  };

  return listing as ExtendedListing;
}

/**
 * Flattens a V3 building group into a deduplicated `ExtendedListing[]` for the
 * SRP grid + map markers. V3 nests one-or-more card entries under each building;
 * dedup mirrors `normalizeListings` (by `generateMarkerId`).
 */
export function normalizeV3Listings(
  group: HpBuildingGroupDto | null | undefined,
): Array<ExtendedListing> {
  const buildings = group?.buildings ?? [];
  const listings = buildings.flatMap((building) =>
    (building.listings ?? []).map((card) => toCardListing(card, building)),
  );
  return Array.from(
    new Map(listings.map((listing) => [generateMarkerId(listing), listing])).values(),
  );
}
